import React, { useState, useEffect, useRef } from "react";
import API_BASE_URL from "../../../../../Constants";

const TaskTypes = [
  { key: "perform", value: "Perform" },
  { key: "witness", value: "Witness" },
  { key: "both", value: "Both" },
];
const InspYears = [2023, 2024, 2025];

/**
 * InspectionWizardModal
 */
export default function InspectionWizardModal({ onClose, refreshList }) {
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Building
  const [allBuildings, setAllBuildings] = useState([]);
  const [buildingId, setBuildingId] = useState(0);
  const [agencyId, setAgencyId] = useState(null);

  // Step 2: Form details
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [managementCompanyId, setManagementCompanyId] = useState("");

  const [agencyTests, setAgencyTests] = useState([]);
  const [testId, setTestId] = useState("");

  const [taskType, setTaskType] = useState("perform");
  const [approvalDate, setApprovalDate] = useState("");
  const [inspYear, setInspYear] = useState("");

  // "Servicer" is actually an Inspector.
  const [allInspectors, setAllInspectors] = useState([]);
  const [servicer, setServicer] = useState("");

  // Extra fields
  const [hours, setHours] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [lastDate, setLastDate] = useState("");

  // Device selection
  const [devices, setDevices] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);

  // For error & blocking double-submission
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMounted = useRef(true);

  //-------------------------------------------
  // 1) Fetch building list
  //-------------------------------------------
  useEffect(() => {
    isMounted.current = true;

    const fetchBuildings = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/setup/buildings`, {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch buildings. Status: ${res.status}`);
        }
        const data = await res.json();
        const buildingArr = Array.isArray(data) ? data : data.content || [];
        if (isMounted.current) {
          setAllBuildings(buildingArr);
        }
      } catch (err) {
        console.error("Error fetching buildings:", err);
      }
    };

    fetchBuildings();

    return () => {
      isMounted.current = false;
    };
  }, []);

  //-------------------------------------------
  // 2) Fetch building’s devices (after pick building)
  //-------------------------------------------
  useEffect(() => {
    if (!buildingId || buildingId <= 0) {
      setDevices([]);
      return;
    }
    let abort = false;

    const fetchDevices = async () => {
      try {
        const url = `${API_BASE_URL}/api/setup/buildings/devices/${buildingId}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          throw new Error(`Failed to fetch devices. Status: ${res.status}`);
        }
        const data = await res.json();
        if (!abort) {
          setDevices(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error fetching devices:", err);
        if (!abort) setDevices([]);
      }
    };

    fetchDevices();
    return () => {
      abort = true;
    };
  }, [buildingId]);

  //-------------------------------------------
  // 3) Fetch management companies
  //-------------------------------------------
  useEffect(() => {
    const fetchMgmtCompanies = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/setup/managementcompanies/all`,
          { credentials: "include" }
        );
        if (!res.ok) {
          throw new Error(`Failed to fetch mgmt co. Status: ${res.status}`);
        }
        const data = await res.json();
        setManagementCompanies(data || []);
      } catch (err) {
        console.error("Error fetching mgmt co:", err);
      }
    };
    fetchMgmtCompanies();
  }, []);

  //-------------------------------------------
  // 4) When pick building => store building’s agencyId
  //-------------------------------------------
  const handleBuildingSelect = (e) => {
    const chosenId = parseInt(e.target.value, 10) || 0;
    setBuildingId(chosenId);

    const bldgObj = allBuildings.find((b) => b.buildingID === chosenId);
    if (bldgObj && bldgObj.agencyID) {
      setAgencyId(bldgObj.agencyID);
    } else {
      setAgencyId(null);
    }
  };

  //-------------------------------------------
  // 5) Agency tests => if building has an agencyId
  //-------------------------------------------
  useEffect(() => {
    if (!agencyId) {
      setAgencyTests([]);
      setTestId("");
      return;
    }
    let abort = false;

    const fetchTests = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${agencyId}`,
          { credentials: "include" }
        );
        if (!res.ok) {
          throw new Error(`HTTP error! ${res.status}`);
        }
        const data = await res.json();
        if (!abort) {
          setAgencyTests(Array.isArray(data) ? data : []);
          setTestId("");
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchTests();
    return () => {
      abort = true;
    };
  }, [agencyId]);

  //-------------------------------------------
  // 6) Inspectors => for "servicer"
  //-------------------------------------------
  useEffect(() => {
    const fetchInspectors = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/inspectors`, {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch inspectors. Status: ${res.status}`);
        }
        const data = await res.json();
        setAllInspectors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching inspectors:", err);
      }
    };
    fetchInspectors();
  }, []);

  //-------------------------------------------
  // Step nav
  //-------------------------------------------
  const goNext = () => {
    if (currentStep === 1 && buildingId <= 0) {
      alert("Please select a valid building first.");
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };
  const goBack = () => setCurrentStep((prev) => prev - 1);

  // Device checkboxes => store numeric IDs as strings
  const handleDeviceCheckbox = (devId, checked) => {
    const devIdStr = devId.toString();
    setSelectedDeviceIds((prev) =>
      checked ? [...prev, devIdStr] : prev.filter((id) => id !== devIdStr)
    );
  };

  //-------------------------------------------
  // Final “Finish” => POST /api/inspections
  //-------------------------------------------
  const handleFinish = async () => {
    if (isSubmitting) return; // guard to avoid double submission
    setIsSubmitting(true);

    // Make sure required fields are set
    const requiredOk =
      buildingId > 0 &&
      selectedDeviceIds.length > 0 &&
      managementCompanyId &&
      testId &&
      taskType &&
      approvalDate &&
      inspYear &&
      servicer;

    if (!requiredOk) {
      setError(true);
      setIsSubmitting(false);
      return;
    }

    const payload = {
      buildingId,
      agencyId: agencyId ? Number(agencyId) : null, // <-- Include agencyId here
      deviceIds: selectedDeviceIds.map((dStr) => Number(dStr)),
      managementCompanyId: Number(managementCompanyId),
      testId: Number(testId),
      taskType,
      approvalDate,
      servicer,
      nextDate,
      lastDate,
      hours: hours ? Number(hours) : 0,
      inspYear: parseInt(inspYear, 10),
      status: "Active",
    };

    try {
      console.log("Sending payload =>", payload);
      const res = await fetch(`${API_BASE_URL}/api/inspections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // <--- ensure cookies are included
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Non-OK status =>", res.status, errorText);
        throw new Error(`HTTP error! Status: ${res.status}\n${errorText}`);
      }

      alert("Inspection created successfully!");
      if (refreshList) refreshList();
      onClose();
    } catch (err) {
      console.error("Error creating inspection:", err);
      alert(`Error creating inspection: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded shadow-md w-[800px] max-h-[90vh] overflow-auto">
        {/* HEADER */}
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-bold">Add Inspection (Wizard)</h2>
          <button
            onClick={onClose}
            className="px-2 py-1 bg-gray-300 rounded hover:bg-gray-400"
          >
            X
          </button>
        </div>

        {/* STEP 1 */}
        {currentStep === 1 && (
          <div>
            <h3 className="mb-2 font-semibold">Step 1: Select Building</h3>
            <label className="block mb-1 text-gray-700 font-semibold">
              Building
            </label>
            <select
              className="border border-gray-300 w-full p-2 rounded"
              value={buildingId}
              onChange={handleBuildingSelect}
            >
              <option value={0}>-- Select Building --</option>
              {allBuildings.map((b) => (
                <option key={b.buildingID} value={b.buildingID}>
                  {b.buildingReference || `Building #${b.buildingID}`}
                </option>
              ))}
            </select>

            <div className="flex justify-end mt-4">
              <button
                onClick={goNext}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {currentStep === 2 && (
          <div>
            <h3 className="mb-2 font-semibold">Step 2: Fill Details</h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Management Company */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Mgmt Company
                </label>
                <select
                  className="border border-gray-300 w-full p-2 rounded"
                  value={managementCompanyId}
                  onChange={(e) => setManagementCompanyId(e.target.value)}
                >
                  <option value="">-- Select Mgmt Co --</option>
                  {managementCompanies.map((mc) => (
                    <option key={mc.managementId} value={mc.managementId}>
                      {mc.companyName || `Mgmt #${mc.managementId}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Agency Test */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Agency Test
                </label>
                <select
                  className="border border-gray-300 w-full p-2 rounded"
                  disabled={!agencyId}
                  value={testId}
                  onChange={(e) => setTestId(e.target.value)}
                >
                  <option value="">-- Select Test --</option>
                  {agencyTests.map((t) => (
                    <option key={t.testId} value={t.testId}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Task Type */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Task Type
                </label>
                <select
                  className="border border-gray-300 w-full p-2 rounded"
                  value={taskType}
                  onChange={(e) => setTaskType(e.target.value)}
                >
                  {TaskTypes.map((type) => (
                    <option key={type.key} value={type.key}>
                      {type.value}
                    </option>
                  ))}
                </select>
              </div>

              {/* Approval Date */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Approval Date
                </label>
                <input
                  type="date"
                  className="border border-gray-300 w-full p-2 rounded"
                  value={approvalDate}
                  onChange={(e) => setApprovalDate(e.target.value)}
                />
              </div>

              {/* Insp Year */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Inspection Year
                </label>
                <select
                  className="border border-gray-300 w-full p-2 rounded"
                  value={inspYear}
                  onChange={(e) => setInspYear(e.target.value)}
                >
                  <option value="">-- Select Year --</option>
                  {InspYears.map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </select>
              </div>

              {/* Servicer => Inspector */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Servicer (Inspector)
                </label>
                <select
                  className="border border-gray-300 w-full p-2 rounded"
                  value={servicer}
                  onChange={(e) => setServicer(e.target.value)}
                >
                  <option value="">-- Select Inspector --</option>
                  {allInspectors.map((insp) => (
                    <option key={insp.inspectorId} value={insp.inspectorId}>
                      {insp.inspectorName
                        ? `${insp.inspectorName} (#${insp.inspectorId})`
                        : `Inspector #${insp.inspectorId}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Hours */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Hours
                </label>
                <input
                  type="number"
                  className="border border-gray-300 w-full p-2 rounded"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </div>

              {/* Next Date */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Next Date
                </label>
                <input
                  type="date"
                  className="border border-gray-300 w-full p-2 rounded"
                  value={nextDate}
                  onChange={(e) => setNextDate(e.target.value)}
                />
              </div>

              {/* Last Date */}
              <div>
                <label className="block mb-1 text-gray-700 font-semibold">
                  Last Date
                </label>
                <input
                  type="date"
                  className="border border-gray-300 w-full p-2 rounded"
                  value={lastDate}
                  onChange={(e) => setLastDate(e.target.value)}
                />
              </div>
            </div>

            {/* Device(s) checklist */}
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Select Device(s)</h4>
              <div className="border border-gray-300 p-2 rounded max-h-40 overflow-auto">
                {devices.length === 0 ? (
                  <p>No devices found for this building.</p>
                ) : (
                  devices.map((dev) => {
                    const devIdStr = dev.deviceId.toString();
                    const checked = selectedDeviceIds.includes(devIdStr);
                    return (
                      <label
                        key={dev.deviceId}
                        className="flex items-center space-x-2"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            handleDeviceCheckbox(dev.deviceId, e.target.checked)
                          }
                        />
                        <span>{`#${dev.deviceId} - ${
                          dev.deviceType || "Unknown"
                        }`}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {error && (
              <p className="mt-2 text-sm text-red-600">
                Please fill out all required fields (building, mgmt co, test,
                device(s), inspector, etc.) before creating an inspection.
              </p>
            )}

            <div className="flex justify-between mt-4">
              <button
                onClick={goBack}
                className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
              >
                Back
              </button>
              <button
                onClick={handleFinish}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {isSubmitting ? "Submitting..." : "Finish"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
